import { Grid, Skeleton, type SxProps, type Theme } from "@mui/material";

import Button from "../../../../common/button/components/Button";
import {
  BoxButton,
  BoxPrice,
  BoxSubtitle,
  BoxTitle,
  BoxValue,
  Card,
  OfferStamp,
  OfferStampText,
  OldPriceText,
  PriceContent,
  PriceLabel,
  PricePeriod,
  PricePersonText,
  PriceSection,
} from "../styles/ProductsStyles";

import CheckItems from "./CheckItems";
import ProductFooter from "./ProductFooter";
import type { ProductCard } from "./ProductsTypes";
import { hasOldPrice, hasPrice } from "./productsCardUtils";

type OfferStampMode = "price" | "oldPrice";
type PriceLabelMode = "always" | "oldPriceOnly";

export type ProductPlanCardProps = {
  product: ProductCard;
  gtmHandleClick: (planName: string, id: number) => void;
  navigateOut: () => void;
  withMargin?: boolean;
  offerStampMode?: OfferStampMode;
  priceLabelMode?: PriceLabelMode;
  showOldPriceCurrency?: boolean;
  cardSx?: SxProps<Theme>;
};

export function ProductPlanCard({
  product,
  gtmHandleClick,
  navigateOut,
  withMargin = false,
  offerStampMode = "oldPrice",
  priceLabelMode = "oldPriceOnly",
  showOldPriceCurrency = true,
  cardSx,
}: Readonly<ProductPlanCardProps>) {
  const productHasPrice = hasPrice(product);
  const productHasOldPrice = hasOldPrice(product);

  const shouldShowOfferStamp =
    offerStampMode === "price" ? productHasPrice : productHasOldPrice;

  const shouldShowPriceLabel =
    priceLabelMode === "always" ||
    (priceLabelMode === "oldPriceOnly" && productHasOldPrice);

  return (
    <Card
      data-testid="product-card"
      sx={{
        marginBottom: withMargin ? "80px" : "auto",
        ...cardSx,
      }}
    >
      {shouldShowOfferStamp && (
        <OfferStamp>
          <OfferStampText>
            {product.offerLabel || "OFERTA\nEXCLUSIVA"}
          </OfferStampText>
        </OfferStamp>
      )}

      <Grid padding={1}>
        <BoxTitle>{product.title}</BoxTitle>

        <BoxValue>
          <BoxSubtitle>{product.subtitle}</BoxSubtitle>

          {productHasPrice ? (
            <PriceSection>
              {productHasOldPrice && (
                <OldPriceText>
                  DE:{" "}
                  <span>
                    {showOldPriceCurrency ? "R$ " : ""}
                    {product.oldPrice},{product.decimalOldPrice}*
                  </span>
                </OldPriceText>
              )}

              {shouldShowPriceLabel && <PriceLabel>POR:</PriceLabel>}

              <PriceContent>
                <BoxPrice>
                  R$ <span>{product.price}</span>,{product.decimalPrice}*
                </BoxPrice>

                <PricePeriod>/ MÊS</PricePeriod>
              </PriceContent>

              <PricePersonText>mensais por pessoa</PricePersonText>
            </PriceSection>
          ) : (
            <Skeleton width="100%" />
          )}
        </BoxValue>
      </Grid>

      <CheckItems checkItems={product.checkItems} />

      <BoxButton>
        <Button
          type="button"
          variant="contained"
          $background="fill"
          $width="232px"
          $bgcolor="#FF5000"
          onClick={navigateOut}
        >
          Contrate Agora
        </Button>
      </BoxButton>

      <ProductFooter data={product} gtmHandleClick={gtmHandleClick} />
    </Card>
  );
}

export default ProductPlanCard;